/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { GitHubConfig } from '../types';

export interface GitHubRepository {
  name: string;
  full_name: string;
  description: string;
  private: boolean;
  html_url: string;
  clone_url: string;
  owner: {
    login: string;
  };
}

export interface GitHubUser {
  login: string;
  name: string;
  email: string;
  avatar_url: string;
}

export interface GitHubOrganization {
  login: string;
  id: number;
  description: string | null;
  avatar_url: string;
}

export class GitHubService {
  private config: GitHubConfig;
  private baseUrl = 'https://api.github.com';

  constructor(config: GitHubConfig) {
    this.config = config;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = {
      'Authorization': `Bearer ${this.config.accessToken}`,
      'Accept': 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GitHub API error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Parse GitHub Link header to find the next page URL.
   * Link header format: <url>; rel="next", <url>; rel="last"
   */
  private parseNextPageUrl(linkHeader: string | null): string | null {
    if (!linkHeader) return null;
    
    const links = linkHeader.split(',');
    for (const link of links) {
      const match = link.match(/<([^>]+)>;\s*rel="next"/);
      if (match) {
        return match[1];
      }
    }
    return null;
  }

  /**
   * Fetch all pages of a paginated GitHub API endpoint.
   * GitHub API has a max per_page of 100, so we need to paginate to get all results.
   */
  private async fetchAllPages<T>(endpoint: string): Promise<T[]> {
    const results: T[] = [];
    const separator = endpoint.includes('?') ? '&' : '?';
    let url: string | null = `${this.baseUrl}${endpoint}${separator}per_page=100`;
    
    while (url) {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Accept': 'application/vnd.github+json',
          'X-GitHub-Api-Version': '2022-11-28',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`GitHub API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json() as T[];
      results.push(...data);

      url = this.parseNextPageUrl(response.headers.get('Link'));
    }

    return results;
  }

  async testConnection(): Promise<{ success: boolean; error?: string; user?: GitHubUser }> {
    try {
      const user = await this.makeRequest<GitHubUser>('/user');
      return { success: true, user };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to connect to GitHub',
      };
    }
  }

  async getRepositories(): Promise<GitHubRepository[]> {
    try {
      // Get user's repositories (public and private)
      // Uses pagination to fetch all repos since GitHub API max per_page is 100
      const repos = await this.fetchAllPages<GitHubRepository>('/user/repos?sort=updated');
      return repos;
    } catch (error) {
      console.error('Failed to fetch repositories:', error);
      return [];
    }
  }

  async getOrganizationRepositories(org: string): Promise<GitHubRepository[]> {
    try {
      // Uses pagination to fetch all repos since GitHub API max per_page is 100
      const repos = await this.fetchAllPages<GitHubRepository>(`/orgs/${org}/repos?sort=updated`);
      return repos;
    } catch (error) {
      console.error('Failed to fetch organization repositories:', error);
      return [];
    }
  }

  async getOrganizations(): Promise<GitHubOrganization[]> {
    try {
      // Uses pagination to fetch all orgs since GitHub API max per_page is 100
      const orgs = await this.fetchAllPages<GitHubOrganization>('/user/orgs');
      return orgs;
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      return [];
    }
  }

  async getRepository(owner: string, repo: string): Promise<GitHubRepository> {
    return this.makeRequest<GitHubRepository>(`/repos/${owner}/${repo}`);
  }

  async validateRepository(owner: string, repo: string): Promise<boolean> {
    try {
      await this.getRepository(owner, repo);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Create a new GitHub repository
   * This calls the backend endpoint which handles both GitHub API and local clone
   */
  async createRepository(options: {
    name: string;
    description?: string;
    isPrivate?: boolean;
  }): Promise<{
    success: boolean;
    repository?: {
      name: string;
      fullName: string;
      description: string;
      private: boolean;
      htmlUrl: string;
      cloneUrl: string;
      sshUrl: string;
    };
    localPath?: string;
    error?: string;
  }> {
    try {
      const apiUrl = import.meta.env.DEV 
        ? 'http://localhost:3002/api/github/create-repo'
        : '/api/github/create-repo';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: options.name,
          description: options.description || '',
          isPrivate: options.isPrivate ?? true,
          accessToken: this.config.accessToken,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || result.message || 'Failed to create repository',
        };
      }

      return {
        success: true,
        repository: result.repository,
        localPath: result.localPath,
      };
    } catch (error: any) {
      console.error('Failed to create repository:', error);
      return {
        success: false,
        error: error.message || 'Failed to create repository',
      };
    }
  }
}

